import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useIntl } from "react-intl";
import { useMe } from "@/auth/use-me";
import { useAssistantContext } from "@/lib/use-assistant-context";
import { useAssistantTour } from "@/lib/use-assistant-tour";
import { buttonClasses } from "@/components/design/button-classes";
import { getConfig } from "@/lib/config";
import { DeleteAccountCard } from "./settings/DeleteAccountCard";
import { LanguageControl } from "./settings/LanguageControl";
import { ClaudeConnectPanel } from "./settings/ClaudeConnectPanel";
import { McpAppConnectPanel } from "./settings/McpAppConnectPanel";

// Personal account settings, distinct from the system ADMIN settings at
// /admin/settings. Both surfaces are admin-only (gated by requireAdmin); a
// normal user edits their profile under /save instead. Only sections backed
// by genesis are shown: identity + account deletion, and the data export
// hand-off. Notifications/privacy had no backend and were removed.

const ALL_SECTIONS = [
  { id: "account", labelId: "userSettings.sectionAccount", fallback: "Account" },
  { id: "data", labelId: "userSettings.sectionData", fallback: "Data & export" },
  { id: "claude", labelId: "userSettings.sectionConnections", fallback: "Connections" },
] as const;
type SectionId = (typeof ALL_SECTIONS)[number]["id"];

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-ink/5 pb-3 last:border-0 last:pb-0 @sm:flex-row @sm:items-baseline @sm:gap-4">
      <div className="w-40 shrink-0 text-[12px] text-ink/45">{label}</div>
      <div className="text-sm text-ink/80">{value}</div>
    </div>
  );
}

export function UserSettingsPage() {
  const intl = useIntl();
  const me = useMe();
  // The Claude section is shown only where the MCP endpoint is reachable.
  const mcpEnabled = getConfig().mcp.enabled;
  const SECTIONS = mcpEnabled ? ALL_SECTIONS : ALL_SECTIONS.filter((s) => s.id !== "claude");
  // Deep link ?section=claude (the what's-new presentation): seed the active section at first
  // render only (ServiceMenu's deep-link pattern); unknown or gated ids fall back to account.
  const [searchParams] = useSearchParams();
  const [active, setActive] = useState<SectionId>(() => {
    const requested = searchParams.get("section");
    return SECTIONS.some((s) => s.id === requested) ? (requested as SectionId) : "account";
  });

  // Live assistant context (motive 1, @external-adr AGENT-SERVICE-0020-ag-ui-protocol-adoption): the account-settings page + which section is open.
  useAssistantContext([
    {
      description: "the account settings page",
      value: `open section: ${active === "account" ? "identity & account" : "data export"}`,
    },
  ]);

  // Guided tour (/tour): the account-settings page — section nav + the active section's content.
  useAssistantTour({
    label: intl.formatMessage({
      id: "userSettings.tour.label",
      defaultMessage: "Settings",
    }),
    summary: intl.formatMessage({
      id: "userSettings.tour.summary",
      defaultMessage:
        "These are your account settings: identity, interface language, data export and account deletion.",
    }),
    stops: [
      {
        anchor: "settings.nav",
        title: intl.formatMessage({
          id: "userSettings.tour.nav.title",
          defaultMessage: "The sections",
        }),
        description: intl.formatMessage({
          id: "userSettings.tour.nav.desc",
          defaultMessage:
            "Switch between Account (identity, language, deletion) and Data & export (your GDPR data export).",
        }),
      },
      {
        anchor: "settings.content",
        title: intl.formatMessage({
          id: "userSettings.tour.content.title",
          defaultMessage: "The active section",
        }),
        description:
          active === "account"
            ? intl.formatMessage({
                id: "userSettings.tour.content.accountDesc",
                defaultMessage:
                  "Your identity (name and email), the interface language (also used for AI generation) and, at the bottom, account deletion.",
              })
            : intl.formatMessage({
                id: "userSettings.tour.content.dataDesc",
                defaultMessage:
                  "Export your profile and Grow data as a JSON file (GDPR Art. 20). The button hands you over to the data transfer.",
              }),
      },
    ],
  });

  return (
    <main className="w-full max-w-[1600px] px-4 pb-12 pt-6 @sm:px-6 @lg:px-10">
      <div className="mb-2 text-[10px] tracking-[0.25em] text-ink/55">
        {intl.formatMessage({ id: "userSettings.eyebrow", defaultMessage: "Account · Preferences" })}
      </div>
      <h1 className="font-serif text-5xl leading-tight">
        {intl.formatMessage({ id: "userSettings.heading", defaultMessage: "Settings" })}
      </h1>
      <p className="mt-2 max-w-2xl text-sm text-ink/70">
        {intl.formatMessage({
          id: "userSettings.intro",
          defaultMessage: "Manage your account and data export.",
        })}
      </p>

      <section className="mt-8 grid grid-cols-1 items-start gap-6 @lg:grid-cols-[minmax(220px,280px)_minmax(0,1fr)] @lg:gap-8">
        <aside className="space-y-1" data-assistant-anchor="settings.nav">
          {SECTIONS.map((s) => {
            const isActive = s.id === active;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setActive(s.id)}
                className={`flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left text-[14px] tracking-[0.18em] transition ${
                  isActive ? "font-medium text-ink" : "text-ink/55 hover:font-medium hover:text-ink"
                }`}
              >
                <span
                  className={`h-1.5 w-1.5 shrink-0 rounded-full ${
                    isActive ? "bg-ink" : "bg-transparent"
                  }`}
                />
                {intl.formatMessage({ id: s.labelId, defaultMessage: s.fallback })}
              </button>
            );
          })}
        </aside>

        <section className="space-y-6" data-assistant-anchor="settings.content">
          {active === "account" ? (
            <>
              <div className="box bg-surface p-6">
                <div className="text-[10px] tracking-[0.25em] text-ink/55">
                  {intl.formatMessage({ id: "userSettings.identityTitle", defaultMessage: "Identity" })}
                </div>
                <div className="mt-4 space-y-3">
                  <Row
                    label={intl.formatMessage({ id: "userSettings.name", defaultMessage: "Name" })}
                    value={me.fullName || "–"}
                  />
                  <Row
                    label={intl.formatMessage({ id: "userSettings.email", defaultMessage: "Email" })}
                    value={me.email || "–"}
                  />
                  <div className="flex flex-col gap-0.5 border-b border-ink/5 pb-3 last:border-0 last:pb-0 @sm:flex-row @sm:items-baseline @sm:gap-4">
                    <div className="w-40 shrink-0 text-[12px] text-ink/45">
                      {intl.formatMessage({ id: "userSettings.language", defaultMessage: "Language" })}
                    </div>
                    <div className="text-sm text-ink/80">
                      <LanguageControl />
                      <div className="mt-1 text-[11px] text-ink/45">
                        {intl.formatMessage({
                          id: "userSettings.languageHint",
                          defaultMessage: "Also used for AI generation.",
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <DeleteAccountCard />
            </>
          ) : active === "claude" ? (
            <div className="space-y-6" data-assistant-anchor="settings.claude">
              <ClaudeConnectPanel />
              {getConfig().mcp.apps.length > 0 && (
                <McpAppConnectPanel apps={getConfig().mcp.apps} />
              )}
            </div>
          ) : (
            <div className="box bg-surface p-6">
              <div className="field-value font-medium">
                {intl.formatMessage({ id: "userSettings.data.title", defaultMessage: "Data export" })}
              </div>
              <p className="mt-1 max-w-md text-[12px] text-ink/55">
                {intl.formatMessage({
                  id: "userSettings.data.desc",
                  defaultMessage:
                    "Export your profile and Grow data as a JSON file (GDPR Art. 20). The export lives with your service documents.",
                })}
              </p>
              <Link
                to="/save/basics?service=open"
                className={buttonClasses({ variant: "primary", className: "mt-4" })}
              >
                {intl.formatMessage({ id: "userSettings.data.link", defaultMessage: "Go to data transfer ↗" })}
              </Link>
            </div>
          )}
        </section>
      </section>
    </main>
  );
}
